import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SESSION_EXPIRED_EVENT } from '@/lib/sessionRecovery';

const AuthContext = createContext(null);

const RETRYABLE_STATUS_CODES = [408, 425, 429, 500, 502, 503, 504, 520, 522, 524];
const AUTH_ME_RETRY_ATTEMPTS = 2;
const AUTH_ME_TIMEOUT_MS = 5000;
const LOGIN_RETRY_ATTEMPTS = 2;
const LOGIN_TIMEOUT_MS = 8000;
const AUTH_LOADING_WATCHDOG_MS = 12000;
const SESSION_WARNING_LEAD_MS = 2 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (error) => {
  if (!error?.response) return true;
  return RETRYABLE_STATUS_CODES.includes(error.response.status);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionModalReason, setSessionModalReason] = useState('Your session expired. Please log in again to continue without losing your work.');
  const [reloginEmail, setReloginEmail] = useState('');
  const [reloginPassword, setReloginPassword] = useState('');
  const [reloginError, setReloginError] = useState('');
  const [reloginSubmitting, setReloginSubmitting] = useState(false);
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const sessionTimerRefs = useRef({ warning: null, expiry: null });

  const forceLogout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const clearSessionTimers = useCallback(() => {
    if (sessionTimerRefs.current.warning) {
      window.clearTimeout(sessionTimerRefs.current.warning);
      sessionTimerRefs.current.warning = null;
    }
    if (sessionTimerRefs.current.expiry) {
      window.clearTimeout(sessionTimerRefs.current.expiry);
      sessionTimerRefs.current.expiry = null;
    }
  }, []);

  const decodeTokenExpiry = useCallback((jwtToken) => {
    if (!jwtToken) return null;
    try {
      const [, payload] = jwtToken.split('.');
      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const decoded = JSON.parse(window.atob(normalized));
      return typeof decoded?.exp === 'number' ? decoded.exp * 1000 : null;
    } catch (error) {
      console.warn('Unable to decode token expiry', error);
      return null;
    }
  }, []);

  const openSessionExpiredModal = useCallback((reason) => {
    setSessionModalReason(reason || 'Your session expired. Please log in again to continue without losing your work.');
    setReloginEmail((prev) => prev || user?.email || '');
    setReloginPassword('');
    setReloginError('');
    setSessionWarningOpen(false);
    setSessionModalOpen(true);
  }, [user?.email]);

  const scheduleSessionTimers = useCallback((jwtToken) => {
    clearSessionTimers();

    const expiresAt = decodeTokenExpiry(jwtToken);
    if (!expiresAt) return;

    const msUntilExpiry = expiresAt - Date.now();
    if (msUntilExpiry <= 0) {
      openSessionExpiredModal('Your session expired. Please log in again to continue without losing your work.');
      return;
    }

    const warningDelay = msUntilExpiry - SESSION_WARNING_LEAD_MS;
    if (warningDelay > 0) {
      sessionTimerRefs.current.warning = window.setTimeout(() => {
        setSessionWarningOpen(true);
      }, warningDelay);
    } else {
      setSessionWarningOpen(true);
    }

    sessionTimerRefs.current.expiry = window.setTimeout(() => {
      openSessionExpiredModal('Your session has expired. Please log in again to continue without losing your work.');
    }, msUntilExpiry);
  }, [clearSessionTimers, decodeTokenExpiry, openSessionExpiredModal]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const reason = event?.detail?.reason || event?.detail?.message;
      openSessionExpiredModal(reason);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [openSessionExpiredModal]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      scheduleSessionTimers(token);
      const watchdog = window.setTimeout(() => {
        setLoading(false);
      }, AUTH_LOADING_WATCHDOG_MS);
      fetchUser();

      return () => {
        window.clearTimeout(watchdog);
        clearSessionTimers();
      };
    } else {
      clearSessionTimers();
      setSessionWarningOpen(false);
      setLoading(false);
    }
  }, [token, clearSessionTimers, scheduleSessionTimers]);

  const fetchUser = async () => {
    let lastError = null;

    try {
      for (let attempt = 1; attempt <= AUTH_ME_RETRY_ATTEMPTS; attempt += 1) {
        try {
          const response = await axios.get(`${getApiBaseUrl()}/api/auth/me`, { timeout: AUTH_ME_TIMEOUT_MS });
          setUser(response.data);
          return;
        } catch (error) {
          lastError = error;
          if (!isRetryableError(error) || attempt === AUTH_ME_RETRY_ATTEMPTS) {
            throw error;
          }
          await wait(450 * attempt);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only clear session for true auth failures; transient wake-up/network errors should not force logout.
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        openSessionExpiredModal('Your session expired. Please log in again to continue.');
      } else if (lastError) {
        console.warn('Transient auth check failure; preserving token for retry on next action.');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    let response = null;

    for (let attempt = 1; attempt <= LOGIN_RETRY_ATTEMPTS; attempt += 1) {
      try {
        response = await axios.post(
          `${getApiBaseUrl()}/api/auth/login`,
          { email, password },
          { timeout: LOGIN_TIMEOUT_MS },
        );
        break;
      } catch (error) {
        if (!isRetryableError(error) || attempt === LOGIN_RETRY_ATTEMPTS) {
          throw error;
        }
        await wait(500 * attempt);
      }
    }

    if (!response) {
      throw new Error('Authentication service unavailable');
    }

    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${getApiBaseUrl()}/api/auth/register`, {
      name,
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearSessionTimers();
    forceLogout();
    setSessionWarningOpen(false);
    setSessionModalOpen(false);
  };

  const reauthenticate = async () => {
    if (!reloginEmail || !reloginPassword) {
      setReloginError('Enter email and password to continue.');
      return false;
    }

    try {
      setReloginSubmitting(true);
      setReloginError('');
      await login(reloginEmail, reloginPassword);
      setReloginPassword('');
      setSessionWarningOpen(false);
      setSessionModalOpen(false);
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message =
        (Array.isArray(detail) && detail.map((item) => item?.msg).filter(Boolean).join(', ')) ||
        (typeof detail === 'string' ? detail : null) ||
        error?.message ||
        'Login failed. Please try again.';
      setReloginError(message);
      return false;
    } finally {
      setReloginSubmitting(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, reauthenticate, sessionModalOpen, openSessionExpiredModal }}>
      {children}

      <Dialog open={sessionModalOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" data-testid="session-expired-dialog">
          <DialogHeader>
            <DialogTitle data-testid="session-expired-title">Session expired</DialogTitle>
            <DialogDescription data-testid="session-expired-description">
              {sessionModalReason}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="session-relogin-email" data-testid="session-expired-email-label">Email</Label>
              <Input
                id="session-relogin-email"
                value={reloginEmail}
                onChange={(e) => setReloginEmail(e.target.value)}
                data-testid="session-expired-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="session-relogin-password" data-testid="session-expired-password-label">Password</Label>
              <Input
                id="session-relogin-password"
                type="password"
                value={reloginPassword}
                onChange={(e) => setReloginPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    reauthenticate();
                  }
                }}
                data-testid="session-expired-password-input"
              />
            </div>
            {reloginError ? (
              <p className="text-sm text-destructive" data-testid="session-expired-error">{reloginError}</p>
            ) : null}
          </div>

          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={logout} data-testid="session-expired-logout-button">
              Log out
            </Button>
            <Button onClick={reauthenticate} disabled={reloginSubmitting} data-testid="session-expired-submit-button">
              {reloginSubmitting ? 'Logging in...' : 'Log in to continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sessionWarningOpen && !sessionModalOpen} onOpenChange={setSessionWarningOpen}>
        <DialogContent className="max-w-md" data-testid="session-warning-dialog">
          <DialogHeader>
            <DialogTitle data-testid="session-warning-title">Session ending soon</DialogTitle>
            <DialogDescription data-testid="session-warning-description">
              Your session will expire in about 2 minutes. Please log in again now to keep working without interruption.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="session-warning-email" data-testid="session-warning-email-label">Email</Label>
              <Input
                id="session-warning-email"
                value={reloginEmail}
                onChange={(e) => setReloginEmail(e.target.value)}
                data-testid="session-warning-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="session-warning-password" data-testid="session-warning-password-label">Password</Label>
              <Input
                id="session-warning-password"
                type="password"
                value={reloginPassword}
                onChange={(e) => setReloginPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    reauthenticate();
                  }
                }}
                data-testid="session-warning-password-input"
              />
            </div>
            {reloginError ? (
              <p className="text-sm text-destructive" data-testid="session-warning-error">{reloginError}</p>
            ) : null}
          </div>

          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setSessionWarningOpen(false)} data-testid="session-warning-dismiss-button">
              Continue for now
            </Button>
            <Button onClick={reauthenticate} disabled={reloginSubmitting} data-testid="session-warning-submit-button">
              {reloginSubmitting ? 'Logging in...' : 'Log in now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
